import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { environment } from '@environments';
import { SETTINGS_ENDPOINT } from '@models/common.models';
import {
  Observable,
  map,
  catchError,
  tap,
  iif,
  of,
  filter,
  switchMap,
} from 'rxjs';
import { HttpErrorHandlerService } from './http-error-handler.service';
import { Setting, Settings } from '@models/webapi/settings';
import { EytcAuthService } from '@services/authentication/eytc-auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  readonly #http = inject(HttpClient);
  readonly #httpErrorHandler = inject(HttpErrorHandlerService);
  readonly #authService = inject(EytcAuthService);
  readonly #webApiUrl = environment.apiConfig.baseUrl;
  readonly #destroyRef: DestroyRef = inject(DestroyRef);

  #settings: Settings = new Settings([]);

  public getAll(): Observable<Settings> {
    return iif(
      () => this.#settings?.hasAny() ?? false,
      of(this.#settings),
      this.#load()
    ).pipe(takeUntilDestroyed(this.#destroyRef));
  }

  #load(): Observable<Settings> {
    return this.#authService.isUserLogged$.pipe(
      // IdleService will try to retrieve settings even on anonymous screens (login, logout, etc.)
      // so we need to filter out those cases
      filter(isLogged => isLogged),
      switchMap(() =>
        this.#http
          .get<Setting[]>(`${this.#webApiUrl}${SETTINGS_ENDPOINT}`)
          .pipe(
            map(response => Settings.from(response)),
            tap(response => (this.#settings = response)),
            catchError((error: unknown) => {
              this.#httpErrorHandler.handleError(error);
              throw error;
            })
          )
      )
    );
  }
}
